import { useEffect, useState } from 'react';
import { platformApi, PlatformApiError } from '../lib/platformApi/client';
import { ConfirmDialog } from './ConfirmDialog';
import { DeleteTenantDialog } from './DeleteTenantDialog';
import { TenantBackupsPanel } from './TenantBackupsPanel';
import type { PlatformTenant } from './types';

interface TenantDetailScreenProps {
  tenantId: number;
  onBack: () => void;
  onOpenAiPlan: (slug: string, name: string) => void;
}

// Discriminates which of the two confirm-gated actions is pending, so one
// ConfirmDialog instance (see its own header comment for why window.confirm
// was replaced) can serve both call sites without duplicating markup.
type PendingConfirm =
  | { kind: 'status'; next: 'active' | 'suspended' }
  | { kind: 'remove-domain'; id: number; hostname: string }
  | null;

export default function TenantDetailScreen({ tenantId, onBack, onOpenAiPlan }: TenantDetailScreenProps) {
  const [tenant, setTenant] = useState<PlatformTenant | null>(null);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [domainError, setDomainError] = useState('');
  const [addingDomain, setAddingDomain] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const refresh = () => {
    platformApi.get<PlatformTenant>(`/tenants/${tenantId}`)
      .then(t => { setTenant(t); setName(t.name); })
      .catch(e => setError(e instanceof Error ? e.message : String(e)));
  };

  useEffect(refresh, [tenantId]);

  const saveName = async () => {
    if (!tenant || name.trim() === tenant.name) return;
    setSaving(true);
    try {
      await platformApi.patch(`/tenants/${tenantId}`, { name: name.trim() });
      refresh();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setSaving(false);
  };

  const applyStatusChange = async (next: 'active' | 'suspended') => {
    try {
      await platformApi.patch(`/tenants/${tenantId}`, { status: next });
      refresh();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const applyRemoveDomain = async (id: number) => {
    try {
      await platformApi.delete(`/domains/${id}`);
      refresh();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const addDomain = async () => {
    const hostname = newDomain.trim().toLowerCase();
    if (!hostname) return;
    setAddingDomain(true); setDomainError('');
    try {
      await platformApi.post(`/tenants/${tenantId}/domains`, { hostname });
      setNewDomain('');
      refresh();
    } catch (e) {
      setDomainError(e instanceof PlatformApiError ? e.message : (e instanceof Error ? e.message : String(e)));
    }
    setAddingDomain(false);
  };

  if (error) {
    return (
      <div style={{ maxWidth: 720, margin: '40px auto', padding: '0 20px' }}>
        <button onClick={onBack} style={{ marginBottom: 16, background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer' }}>← Back to tenants</button>
        <div style={{ padding: '8px 12px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, color: '#991B1B', fontSize: 13 }}>{error}</div>
      </div>
    );
  }
  if (!tenant) return <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Loading…</div>;

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 20px' }}>
      <button onClick={onBack} style={{ marginBottom: 16, background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', padding: 0 }}>← Back to tenants</button>

      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>{tenant.slug}</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <label className="lbl">Hospital name</label>
            <input value={name} onChange={e => setName(e.target.value)} onBlur={saveName} disabled={saving} />
          </div>
        </div>
        <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 14 }}>
          Schema: <code>{tenant.db_schema}</code> · Plan: {tenant.plan} · Status: <strong>{tenant.status}</strong>
        </div>
        {tenant.status !== 'deleted' && (
          <div style={{ marginBottom: 14 }}>
            <button onClick={() => onOpenAiPlan(tenant.slug, tenant.name)} style={{ background: 'var(--bg)', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}>
              🤖 AI plan &amp; usage
            </button>
          </div>
        )}
        {(tenant.status === 'active' || tenant.status === 'suspended') && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button
              onClick={() => setPendingConfirm({ kind: 'status', next: tenant.status === 'suspended' ? 'active' : 'suspended' })}
              style={{
                background: tenant.status === 'suspended' ? '#DCFCE7' : '#FEE2E2',
                color: tenant.status === 'suspended' ? '#166534' : '#991B1B',
                border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, cursor: 'pointer', fontWeight: 600,
              }}
            >
              {tenant.status === 'suspended' ? 'Reactivate tenant' : 'Suspend tenant'}
            </button>
            {/* Only reachable once already suspended — a deliberate two-step
                process (suspend now, delete later) rather than one click
                destroying a live tenant's data. See DeleteTenantDialog's own
                header comment. */}
            {tenant.status === 'suspended' && (
              <button
                onClick={() => setDeleteDialogOpen(true)}
                style={{ background: 'none', border: '1px solid #FECACA', color: '#DC2626', borderRadius: 8, padding: '6px 14px', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}
              >
                Delete tenant permanently
              </button>
            )}
          </div>
        )}
        {tenant.status === 'deleted' && (
          <div style={{ fontSize: 12, color: '#9CA3AF' }}>
            Deleted{tenant.deleted_at ? ` on ${new Date(tenant.deleted_at).toLocaleString()}` : ''}
            {tenant.dump_storage_key && <> — backup at <code>{tenant.dump_storage_key}</code></>}
          </div>
        )}
      </div>

      <div className="card" style={{ padding: 20 }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>Custom domains</div>
        <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 14 }}>
          In addition to <code>{tenant.slug}.&lt;your-base-domain&gt;</code>, which always works. DNS for a custom domain
          (pointing it at this deployment) and TLS must be set up separately outside this console — adding it here only
          makes the app route it to the right tenant once traffic arrives.
        </div>

        {(tenant.custom_domains ?? []).map(d => (
          <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>{d.hostname}</span>
            <button onClick={() => setPendingConfirm({ kind: 'remove-domain', id: d.id, hostname: d.hostname })} style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', fontSize: 12 }}>Remove</button>
          </div>
        ))}
        {(tenant.custom_domains ?? []).length === 0 && (
          <div style={{ fontSize: 12, color: '#9CA3AF', padding: '8px 0' }}>No custom domains mapped yet.</div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <input
            value={newDomain}
            onChange={e => setNewDomain(e.target.value)}
            placeholder="hms.kongumedicalcenter.in"
            style={{ flex: 1 }}
            onKeyDown={e => { if (e.key === 'Enter') addDomain(); }}
          />
          <button className="btn-primary" onClick={addDomain} disabled={addingDomain || !newDomain.trim()}>Add</button>
        </div>
        {domainError && <div style={{ color: '#991B1B', fontSize: 12, marginTop: 8 }}>{domainError}</div>}
      </div>

      {(tenant.status === 'active' || tenant.status === 'suspended') && (
        <TenantBackupsPanel tenantId={tenantId} slug={tenant.slug} tenantStatus={tenant.status} onChanged={refresh} />
      )}

      {pendingConfirm?.kind === 'status' && (
        <ConfirmDialog
          open
          onOpenChange={(open) => { if (!open) setPendingConfirm(null); }}
          title={`${pendingConfirm.next === 'suspended' ? 'Suspend' : 'Reactivate'} tenant '${tenant.slug}'?`}
          description={
            pendingConfirm.next === 'suspended'
              ? 'This takes effect immediately — every request to this tenant (its subdomain and any custom domains) will be refused until reactivated. No data is deleted; this is fully reversible.'
              : 'This takes effect immediately — the tenant becomes reachable again.'
          }
          confirmLabel={pendingConfirm.next === 'suspended' ? 'Suspend' : 'Reactivate'}
          variant={pendingConfirm.next === 'suspended' ? 'danger' : 'primary'}
          onConfirm={() => applyStatusChange(pendingConfirm.next)}
        />
      )}
      {pendingConfirm?.kind === 'remove-domain' && (
        <ConfirmDialog
          open
          onOpenChange={(open) => { if (!open) setPendingConfirm(null); }}
          title={`Remove custom domain "${pendingConfirm.hostname}"?`}
          description="Requests to it will 404 immediately after — the tenant itself and its other domains are unaffected."
          confirmLabel="Remove"
          onConfirm={() => applyRemoveDomain(pendingConfirm.id)}
        />
      )}

      <DeleteTenantDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        tenantId={tenantId}
        slug={tenant.slug}
        onDeleted={onBack}
      />
    </div>
  );
}
